import clsx from 'clsx'
import {
  postQuestionnaireArchive,
  postQuestionnaireCancel,
  postQuestionnaireUnArchive,
} from 'components/QuestionnaireRequestAutomated/api/processor'
import React, { Fragment, useId, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import CancelRequestModal from '../modal/cancelRequestModal'
import {
  archiveQuestionnaire,
  selectCurrentStatus,
  unarchiveQuestionnaire,
  updateCurrentStatus,
  updateStatuses,
} from '../slices/globalSlice'
import AutoQuestionnaireStatus, { AutoQuestionnaireStatuses } from './Statuses'
import TasksSection from './TasksSection'
import domecileMapImage from '/app/assets/images/domecile_map_request.png'
import RequestCanceledModal from '../modal/RequestCanceledModal'
import { isBlank } from '../../../../lib/utilities'
import ClampedLink from './ClampedLink'

const Divider = () => <div style={{ height: '1px', margin: '18px 0px', background: '#F0F0F0', width: '100%' }}></div>

const PropertyInfo = () => {
  const { address, unit } = useSelector((state) => state.global.propertyAddress)
  const buildingUrl = useSelector((state) => state.global.buildingUrl)
  const buildingId = useSelector((state) => state.global.buildingId)
  const { city, state, zip } = useSelector((state) => state.global.buildingAddress)

  return (
    <div className="dome-d-flex dome-flex-column dome-gap6 dome-align-start">
      <div className="dome-d-flex dome-gap12 dome-full-width">
        <img width={113} height={78} style={{ borderRadius: '6px' }} src={domecileMapImage} />
        <div style={{ minWidth: 0 }} className="dome-d-flex dome-flex-column dome-justify-center">
          <ClampedLink
            text={address}
            href={isBlank(buildingUrl) ? `/buildings/${buildingId}` : buildingUrl}
            target="_blank"
            lines={2}
          />
          <span className="dome-p2 dome-color-dark-grey dome-overflow-ellipsis">{unit}</span>
          <span className="dome-p2 dome-color-dark-grey dome-overflow-ellipsis">
            {city}, {state}, {zip}
          </span>
        </div>
      </div>
    </div>
  )
}

const RequesterInfo = () => {
  const { name, company, email, phoneNumber } = useSelector((state) => state.global.requesterInfo)

  return (
    <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
      {[name, company, email, phoneNumber].map((item, index) => (
        <Fragment key={index}>
          {isBlank(item) ? null : <li className="dome-p2 dome-color-dark-grey dome-overflow-ellipsis">{item}</li>}
        </Fragment>
      ))}
    </ul>
  )
}

const RequesterInfoCollapse = () => {
  const comment = useSelector((state) => state.global.comment)

  return (
    <Collapse defaultValue={true} title="requester info">
      <div className="dome-d-flex dome-flex-column dome-gap6">
        {comment && (
          <div style={{ padding: '6px 12px', background: '#F9F9F9', marginTop: 6 }}>
            <span style={{ overflowWrap: 'break-word' }} className="dome-p2 dome-color-dark-grey">
              {comment}
            </span>
          </div>
        )}
        <div style={{ paddingLeft: 12 }}>
          <RequesterInfo />
        </div>
      </div>
    </Collapse>
  )
}

export const Collapse = ({ defaultValue = false, title, titleClassName, children }) => {
  const id = useId()
  const [expand, setExpand] = useState(defaultValue)

  return (
    <div className="processing-page-collapse">
      <div
        aria-controls={id}
        data-toggle="collapse"
        href={`#${CSS.escape(id)}`}
        aria-expanded={defaultValue}
        onClick={() => setExpand(!expand)}
      >
        <div className="dome-d-flex dome-gap6 dome-align-center dome-cursor-pointer">
          <p className={clsx(titleClassName ?? 'dome-p3 dome-color-med-grey')}>{title}</p>
          <span aria-expanded={expand} className="processing-page-collapse-icon">
            <i style={{ fontSize: 13 }} className="fa-thin fa-chevron-down dome-color-med-grey"></i>
          </span>
        </div>
      </div>
      <div className={`collapse ${defaultValue ? 'in' : ''}`} id={id}>
        {children}
      </div>
    </div>
  )
}

const FormActions = () => {
  const dispatch = useDispatch()
  const hashedId = useSelector((state) => state.global.hashedId)
  const purchaserToken = useSelector((state) => state.global.purchaserToken)
  const currentStatus = useSelector(selectCurrentStatus)
  const hasArchived = currentStatus === 'archived'
  const hasCanceled = currentStatus === 'canceled'
  const [openCancelModal, setOpenCancelModal] = useState(false)
  const [openRequestCanceledModal, setOpenRequestCanceledModal] = useState(false)

  const cancelRequest = async () => {
    try {
      const res = await postQuestionnaireCancel(hashedId)
      const { statuses, current_status } = res.data
      dispatch(updateStatuses(statuses))
      dispatch(updateCurrentStatus(current_status))
      setOpenCancelModal(false)
      setOpenRequestCanceledModal(true)
    } catch (error) {
      console.log('error', error)
    }
  }

  const goToDashboardList = () => (window.location = '/questionnaire_requests/list')

  const archiveRequest = () => dispatch(archiveQuestionnaire({ questionnaireRequestId: hashedId, purchaserToken }))
  const unArchiveRequest = () => dispatch(unarchiveQuestionnaire({ questionnaireRequestId: hashedId, purchaserToken }))
  const handleArchive = () => (hasArchived ? unArchiveRequest() : archiveRequest())

  return (
    <Fragment>
      {!hasCanceled && (
        <>
          <Divider />
          <div className="dome-d-flex dome-justify-between">
            <button
              type="button"
              onClick={handleArchive}
              className={clsx('dome-btn dome-btn-link', hasArchived ? 'dome-btn-link-aqua' : 'dome-btn-link-med-grey')}
            >
              {hasArchived ? 'un-archive request' : 'archive request >'}
            </button>
            <button
              type="button"
              onClick={() => setOpenCancelModal(true)}
              className="dome-btn dome-btn-link dome-btn-link-delete"
            >
              cancel
            </button>
          </div>
        </>
      )}
      <CancelRequestModal open={openCancelModal} onSubmit={cancelRequest} onClose={() => setOpenCancelModal(false)} />
      <RequestCanceledModal
        open={openRequestCanceledModal}
        onSubmit={goToDashboardList}
        onClose={() => setOpenRequestCanceledModal(false)}
      />
    </Fragment>
  )
}

export default function RequestInfo({ initialAttachments }) {
  const buildingType = useSelector((state) => state.global.buildingType)

  return (
    <div className="processing-page-request-info-detail">
      <div>
        <div className="dome-d-flex dome-flex-column dome-gap6 dome-align-start dome-full-width">
          <div className="dome-d-flex dome-flex-column dome-gap18 dome-full-width">
            <div className="dome-d-flex dome-justify-between">
              <div className="processing-page-building-type dome-d-flex dome-gap3 dome-align-center">
                <span>
                  <i className="fa-solid fa-buildings fa-xs dome-color-dark-grey"></i>
                </span>
                <span className="dome-p2 dome-color-dark-grey">{buildingType}</span>
              </div>
            </div>
            <PropertyInfo />
            <AutoQuestionnaireStatus />
          </div>
        </div>
        <Divider />
        <TasksSection initialAttachments={initialAttachments} />
        <Divider />
        <div className="dome-d-flex dome-flex-column dome-gap12">
          <RequesterInfoCollapse />
          <AutoQuestionnaireStatuses />
        </div>
        <FormActions />
      </div>
    </div>
  )
}
